import * as R from "ramda";
import {
  ChoiceExerciseWrapping,
  CourseComponentTag,
  CourseContent,
  CourseInstructionType,
  CourseUnitView,
  CourseView,
  DefaultNames,
  IAssessment,
  IChoiceExercise,
  IChoiceExerciseItem,
  IClozeExercise,
  IClozeGap,
  IClozeGapLocalized,
  ICourseComponent,
  ICourseContentSettings,
  ICourseInstruction,
  ICourseInstructionContentPart,
  ICourseLocalizedText,
  ICoursePublication,
  ICourseSettings,
  ICourseUnit,
  ICourseVoiceOver,
  IFullCourse,
  IGridPart,
  IMultipleChoiceExercise,
  IQuestionPart,
  isInstruction,
  isUnit,
  NullablePartPosition,
} from "../components/course-author/types";
import { IPageLockSettings } from "../components/pages/CourseAuthor/CourseInstruction/Parts/PageLockSettings";
import { typeTitle } from "../formatters/courseAuthor";
import { PreferredLocale } from "./locales";
import { ICourseComponentPart } from "../App/Analytics/Common";

export type CourseComponentView = CourseView<ICourseComponent, IFullCourse>;
export type CourseUnitListView = CourseView<ICourseUnit[], IFullCourse>;
export type UnitSubUnitsView = CourseView<ICourseUnit[], ICourseUnit>;
export type SubUnitView = CourseView<ICourseUnit, ICourseUnit[]>;

export const fullCourseViews = {
  assessment: courseAssessmentView,
  assessmentSettings: courseAssessmentSettingsView,
  customCompletedMessage: courseCustomCompletedMessageView,
  description: courseDescriptionView,
  isDeleted: courseDeletedView,
  isLocked: courseLockedView,
  isMultiLocale: courseMultiLocaleView,
  localizedDescription: courseLocalizedDescriptionView,
  localizedTitle: courseLocalizedTitleView,
  masterLocale: courseMasterLocaleView,
  masterLocaleDescription: courseMasterLocaleDescriptionView,
  masterLocaleTitle: courseMasterLocaleTitleView,
  nextChildId: courseNextChildIdView,
  nextCustomCompletedMessageId: courseNextCustomCompletedMessageIdView,
  nextTitleId: courseNextTitleIdView,
  publications: coursePublicationsView,
  settings: courseSettingsView,
  style: courseStyleView,
  supportedLocales: courseSupportedLocalesView,
  title: courseTitleView,
  units: courseUnitsView,
  unitsInPath: courseUnitPathView,
};

export const courseUnitViews = {
  assessment: unitAssessmentView,
  assessmentSettings: unitAssessmentSettingsView,
  components: unitComponentsView,
  deleted: unitDeletedView,
  id: unitIdView,
  localizedTitle: unitTitleView,
  nextChildId: unitNextChildIdView,
  subUnits: unitSubUnitsView,
};

export const courseInstructionViews = {
  content: instructionContentView,
  deleted: instructionDeletedView,
  pageLockSettings: pageLockSettingsView,
  title: instructionTitleView,
  type: instructionTypeView,
};

export const courseInstructionPartViews = {
  choiceExercisePart: {
    items: choiceExercisePartItemsView,
    nextItemId: choiceExercisePartNextItemId,
  },
  clozeExercisePart: {
    localizedGaps: clozeExercisePartGapsView,
    localizedTemplate: clozeExercisePartTemplateView,
    nextGapAnswerId: clozeExercisePartNextGapAnswerIdView,
    nextGapHintId: clozeExercisePartNextGapHintIdView,
    nextGapId: clozeExercisePartNextGapIdView,
  },
  exerciseWrapping: {
    localizedRebuke: choiceExercisePartWrappingView("rebuke"),
    localizedReinforcement: choiceExercisePartWrappingView("reinforcement"),
    localizedTaskAfter: choiceExercisePartWrappingView("taskAfter"),
    localizedTaskBefore: choiceExercisePartWrappingView("taskBefore"),
  },
  localizedContent: instructionPartLocalizedContentView,
  localizedVoiceOver: instructionPartLocalizedVoiceOver,
  localizedVoiceOverText: instructionPartLocalizedVoiceOverText,
  markdownText: instructionPartMarkdownView,
  nextMarkdownId: instructionNextMarkdownId,
  nextVoiceOverId: instructionNextVoiceOverId,
  nextVoiceOverTextId: instructionNextVoiceOverTextId,
};

export const courseComponentViews = {
  children: componentChildrenView,
  deleted: componentDeletedView,
  localizedTitle: componentLocalizedTitleView,
  nextTitleId: componentNextTitleIdView,
  tag: componentTagView,
  title: componentTitleView,
};

export const courseAssessmentViews = {
  allowedMistakes: assessmentAllowedMistakesView,
  questions: assessmentQuestionsView,
  requiredCorrect: assessmentRequiredCorrectView,
};

function assessmentAllowedMistakesView(test: Nullable<IAssessment>): Nullable<number> {
  return R.pathOr(null, ["numAllowedMistakes"], test);
}

function assessmentQuestionsView(test: IAssessment): IQuestionPart[] | null {
  return R.pathOr(null, ["questionBank", "elems"], test);
}

function assessmentRequiredCorrectView(test: Nullable<IAssessment>): Nullable<number> {
  return R.pathOr(null, ["numCorrectAnswersRequired"], test);
}

function componentChildrenView(comp: Nullable<ICourseComponent>): ICourseComponent[] {
  return R.pathOr([], ["content", "children", "elems"], comp);
}

function componentDeletedView(comp: Nullable<ICourseComponent>): Nullable<boolean> {
  return R.pathOr(null, ["deleted"], comp);
}

function componentNextTitleIdView(comp: Nullable<ICourseComponent>): Nullable<number> {
  const titleId = R.pathOr(null, ["content", "title", "nextId"], comp);
  const nameId = R.pathOr(null, ["content", "name", "nextId"], comp);
  return R.isNil(titleId) ? nameId : titleId;
}

function componentTagView(comp?: Nullable<ICourseComponent>): CourseComponentTag | string {
  return R.pathOr("none", ["tag"], comp);
}

function componentLocalizedTitleView(locale: PreferredLocale): (comp: Nullable<ICourseComponent>) => Nullable<string> {
  return (comp: ICourseComponent) => {
    if (isUnit(comp)) {
      const titleIdx = R.findIndex(({ locale: loc }) => loc === locale, comp.content.title.elems);
      return R.pathOr(null, ["content", "title", "elems", titleIdx, "value", "text"], comp);
    } else {
      const titleIdx = R.findIndex(({ locale: loc }) => loc === locale, comp.content.name.elems);
      return R.pathOr(null, ["content", "name", "elems", titleIdx, "value", "text"], comp);
    }
  };
}

function componentTitleView(comp: Nullable<ICourseComponent>): Nullable<string> {
  if (isInstruction(comp)) {
    return R.pathOr(typeTitle(comp.content.type), ["content", "name", "elems", 0, "value", "text"], comp);
  }
  return R.pathOr(DefaultNames.Unit, ["content", "title", "elems", 0, "value", "text"], comp);
}

function pageLockSettingsView(instr: Nullable<ICourseInstruction>): Nullable<IPageLockSettings> {
  return R.pathOr(null, ["content", "pageLockSettings"], instr);
}

function instructionPartLocalizedContentView(
  part: ICourseInstructionContentPart,
  locale: PreferredLocale
): Nullable<string> {
  const elems: ICourseLocalizedText[] = R.pathOr([], ["content", "markdown", "elems"], part);
  const idx = R.findIndex(e => e.locale === locale, elems);

  return R.pathOr(null, [idx, "value", "text"], elems);
}

function instructionPartLocalizedVoiceOver(
  part: ICourseInstructionContentPart,
  locale: PreferredLocale
): Nullable<ICourseVoiceOver> {
  const elems: ICourseVoiceOver[] = R.pathOr([], ["content", "voiceOver", "elems"], part);
  return R.find(e => e.locale === locale, elems) || null;
}

function instructionPartLocalizedVoiceOverText(
  part: ICourseInstructionContentPart,
  locale: PreferredLocale
): Nullable<string> {
  const elems: ICourseVoiceOver[] = R.pathOr([], ["content", "voiceOverText", "elems"], part);
  const idx = R.findIndex(e => e.locale === locale, elems);

  return R.pathOr(null, [idx, "value", "text"], elems);
}

function instructionNextMarkdownId(part: ICourseInstructionContentPart): Nullable<number> {
  return R.pathOr(null, ["content", "markdown", "nextId"], part);
}

function instructionNextVoiceOverId(part: ICourseInstructionContentPart): Nullable<number> {
  return R.pathOr(null, ["content", "voiceOver", "nextId"], part);
}

function instructionNextVoiceOverTextId(part: ICourseInstructionContentPart): Nullable<number> {
  return R.pathOr(null, ["content", "voiceOverText", "nextId"], part);
}

function choiceExercisePartItemsView(mcpart: IChoiceExercise | ICourseComponentPart): Nullable<IChoiceExerciseItem[]> {
  return R.pathOr<IChoiceExerciseItem[]>([], ["content", "items", "elems"], mcpart);
}

function choiceExercisePartWrappingView(
  wrapping: ChoiceExerciseWrapping
): (mcpart: IQuestionPart | ICourseComponentPart, currentLocale: PreferredLocale) => Nullable<string> {
  return (mcpart: IMultipleChoiceExercise, currentLocale: PreferredLocale) => {
    const items: ICourseLocalizedText[] = R.pathOr<ICourseLocalizedText[]>(
      [],
      ["content", "wrapping", wrapping, "elems"],
      mcpart
    );

    const item = R.find(textItem => textItem.locale === currentLocale, items);

    if (R.isNil(item)) {
      return null;
    }

    return R.pathOr(null, ["value", "text"], item);
  };
}

function clozeExercisePartGapsView(
  clozePart: IClozeExercise,
  currentLocale: PreferredLocale
): Nullable<IClozeGapLocalized[]> {
  const gaps: IClozeGap[] = R.pathOr<IClozeGap[]>([], ["content", "gaps", "elems"], clozePart);
  return gaps
    .filter(gap => !gap.deleted)
    .map((gap: IClozeGap) => ({
      answers: gap.answers.elems.filter(answer => answer.locale === currentLocale),
      hint: gap.hint.elems.filter(hint => hint.locale === currentLocale),
      id: gap.id,
    }));
}

function clozeExercisePartTemplateView(clozePart: IClozeExercise, currentLocale: PreferredLocale): Nullable<string> {
  const templates: ICourseLocalizedText[] = R.pathOr([], ["content", "template", "elems"], clozePart);
  const template = R.find(t => t.locale === currentLocale, templates) || null;

  return template && template.value.text;
}

function clozeExercisePartNextGapAnswerIdView(clozePart: IClozeExercise, gapId: number): Nullable<number> {
  const gaps: IClozeGap[] = R.pathOr<IClozeGap[]>([], ["content", "gaps", "elems"], clozePart);
  return R.pathOr(
    null,
    ["answers", "nextId"],
    R.find(g => g.id === gapId, gaps)
  );
}

function clozeExercisePartNextGapHintIdView(clozePart: IClozeExercise, gapId: number): Nullable<number> {
  const gaps: IClozeGap[] = R.pathOr<IClozeGap[]>([], ["content", "gaps", "elems"], clozePart);
  return R.pathOr(
    null,
    ["hint", "nextId"],
    R.find(g => g.id === gapId, gaps)
  );
}

function clozeExercisePartNextGapIdView(clozePart: IClozeExercise): Nullable<number> {
  return R.pathOr(null, ["content", "gaps", "nextId"], clozePart);
}

function choiceExercisePartNextItemId(mcpart: IChoiceExercise): Nullable<number> {
  return R.pathOr(null, ["content", "items", "nextId"], mcpart);
}

function instructionPartMarkdownView(part: Nullable<ICourseInstructionContentPart>): Nullable<string> {
  return R.pathOr(null, ["content", "markdown", "elems", 0, "value", "text"], part);
}

function instructionDeletedView(instr: Nullable<ICourseInstruction>): Nullable<boolean> {
  return R.pathOr(null, ["deleted"], instr);
}

function instructionContentView(instr: Nullable<ICourseInstruction>): ICourseInstructionContentPart[] | null {
  return R.pathOr(null, ["content", "parts", "elems"], instr);
}

function instructionTitleView(instr: Nullable<ICourseInstruction>): string {
  return R.pathOr(null, ["content", "name", "elems", 0, "value", "text"], instr);
}

function instructionTypeView(instr: Nullable<ICourseInstruction>): Nullable<CourseInstructionType> {
  return R.pathOr(null, ["content", "type"], instr);
}

function unitAssessmentSettingsView(unit: Nullable<ICourseUnit>): Nullable<ICourseContentSettings> {
  return R.pathOr(null, ["content", "settings"], unit);
}

function unitAssessmentView(unit: Nullable<ICourseUnit>): Nullable<IAssessment> {
  return R.pathOr(null, ["content", "test"], unit);
}

function unitSubUnitsView(unit?: Nullable<ICourseUnit>): ICourseUnit[] | null {
  const components = unitComponentsView(unit);

  if (R.isNil(components)) {
    return null;
  }

  return R.filter((comp: ICourseComponent) => comp.tag === CourseComponentTag.Unit, components) as ICourseUnit[];
}

function unitComponentsView(unit?: Nullable<ICourseUnit>): ICourseComponent[] | null {
  return R.pathOr(null, ["content", "children", "elems"], unit);
}

function unitDeletedView(unit: Nullable<ICourseUnit>): Nullable<boolean> {
  return R.pathOr(null, ["deleted"], unit);
}

function unitIdView(unit: ICourseUnit): Nullable<number> {
  return R.pathOr(null, ["id"], unit);
}

function unitNextChildIdView(unit: Nullable<ICourseUnit>): Nullable<number> {
  return R.pathOr(null, ["content", "children", "nextId"], unit);
}

function unitTitleView(unit: Nullable<ICourseUnit>, locale: PreferredLocale): Nullable<string> {
  const titles: ICourseLocalizedText[] = R.pathOr<ICourseLocalizedText[]>([], ["content", "title", "elems"], unit);

  const idx = R.findIndex((a: ICourseLocalizedText) => a.locale === locale, titles);

  if (idx < 0) {
    return null;
  }

  const title = R.pathOr(null, [idx, "value", "text"], titles);

  return title;
}

function courseTitleView(course: IFullCourse): Nullable<string> {
  return R.pathOr(null, ["state", "meta", "title"], course);
}

function coursePublicationsView(course: IFullCourse): ICoursePublication[] | null {
  return R.pathOr(null, ["state", "publications", "elems"], course);
}

function courseSettingsView(course: IFullCourse): Nullable<ICourseSettings> {
  return R.pathOr(null, ["state", "content", "value", "settings"], course);
}

function courseNextChildIdView(course: IFullCourse): Nullable<number> {
  return R.pathOr(null, ["state", "content", "value", "root", "children", "nextId"], course);
}

function courseNextTitleIdView(course: IFullCourse): number | null {
  return R.pathOr(null, ["state", "meta", "mLTitle", "nextId"], course);
}

function courseNextCustomCompletedMessageIdView(course: IFullCourse): Nullable<number> {
  return R.pathOr(null, ["state", "content", "value", "settings", "customCompletedMessage", "nextId"], course);
}

function courseStyleView(course: IFullCourse): Nullable<string> {
  return R.pathOr(null, ["state", "style"], course);
}

function courseSupportedLocalesView(course: IFullCourse): PreferredLocale[] | null {
  return R.pathOr(null, ["state", "localeSettings", "supportedLocales"], course);
}

function courseMasterLocaleDescriptionView(course: IFullCourse): Nullable<string> {
  const descriptions: ICourseLocalizedText[] | null = R.pathOr(
    null,
    ["state", "meta", "mLDescription", "elems"],
    course
  );
  const masterLocale: Nullable<PreferredLocale> = courseMasterLocaleView(course);

  if (R.isNil(descriptions) || R.isNil(masterLocale)) {
    return null;
  }

  const idx: number = R.findIndex(lt => lt.locale === masterLocale, descriptions);

  if (idx < 0) {
    return null;
  }

  return descriptions[idx].value.text;
}

function courseMasterLocaleTitleView(course: IFullCourse): Nullable<string> {
  const titles: ICourseLocalizedText[] | null = R.pathOr(null, ["state", "meta", "mLTitle", "elems"], course);
  const masterLocale: Nullable<PreferredLocale> = courseMasterLocaleView(course);

  if (R.isNil(titles) || R.isNil(masterLocale)) {
    return null;
  }

  const idx: number = R.findIndex(lt => lt.locale === masterLocale, titles);

  if (idx < 0) {
    return null;
  }

  return titles[idx].value.text;
}

function courseMasterLocaleView(course: IFullCourse): Nullable<PreferredLocale> {
  return R.pathOr(null, ["state", "localeSettings", "masterLocale"], course);
}

function courseDescriptionView(course: IFullCourse): Nullable<string> {
  return R.pathOr<Nullable<string>>(null, ["state", "meta", "description"], course);
}

function courseAssessmentSettingsView(
  course: IFullCourse,
  unitPath: CourseUnitView[] = []
): Nullable<ICourseContentSettings> {
  const unitView = R.last(unitPath);
  const settings = unitView
    ? courseUnitViews.assessmentSettings(unitView(course))
    : R.pathOr(null, ["state", "content", "value", "root", "settings"], course);
  return settings;
}

function courseAssessmentView(course: IFullCourse, unitPath: CourseUnitView[] = []): Nullable<IAssessment> {
  const unitView = R.last(unitPath);
  const assessment = unitView
    ? courseUnitViews.assessment(unitView(course))
    : R.pathOr(null, ["state", "content", "value", "root", "test"], course);
  return assessment;
}

function courseCustomCompletedMessageView(course: IFullCourse, locale: PreferredLocale): Nullable<string> {
  const messages: ICourseLocalizedText[] =
    R.pathOr<ICourseLocalizedText[] | null>(
      null,
      ["state", "content", "value", "settings", "customCompletedMessage", "elems"],
      course
    ) || [];
  const message = R.find(m => m.locale === locale, messages);
  return message ? message.value.text : null;
}

function courseLocalizedDescriptionView(course: IFullCourse, locale: PreferredLocale): Nullable<string> {
  const descriptions: ICourseLocalizedText[] = R.pathOr<ICourseLocalizedText[] | null>(
    null,
    ["state", "meta", "mLDescription", "elems"],
    course
  );

  if (R.isNil(descriptions)) {
    return null;
  }

  const idx = R.findIndex((a: ICourseLocalizedText) => a.locale === locale, descriptions);

  if (idx < 0) {
    return null;
  }

  return descriptions[idx].value.text;
}

function courseLocalizedTitleView(course: IFullCourse, locale: PreferredLocale): Nullable<string> {
  const titles: ICourseLocalizedText[] = R.pathOr<ICourseLocalizedText[]>(
    [],
    ["state", "meta", "mLTitle", "elems"],
    course
  );

  const idx = R.findIndex((a: ICourseLocalizedText) => a.locale === locale, titles);

  if (idx < 0) {
    return null;
  }

  const title = R.pathOr(null, [idx, "value", "text"], titles);

  return title;
}

function courseDeletedView(course: IFullCourse): Nullable<boolean> {
  return R.pathOr(null, ["state", "deleted"], course);
}

function courseLockedView(course: IFullCourse): Nullable<boolean> {
  return R.pathOr<Nullable<boolean>>(null, ["state", "locked"], course);
}

function courseMultiLocaleView(course: IFullCourse): boolean {
  return R.pathOr(null, ["state", "content", "type"], course) === CourseContent.MultiLocale;
}

function courseUnitPathView(course: IFullCourse, path: CourseUnitView[]): Array<Nullable<ICourseUnit>> {
  return path.map(view => view(course));
}

function courseUnitsView(course: IFullCourse): ICourseUnit[] | null {
  return R.pathOr<ICourseUnit[] | null>(null, ["state", "content", "value", "root", "children", "elems"], course);
}

export function composeView<T, U extends object, V extends object>(
  view2: CourseView<T, U>,
  view1: CourseView<U, V>
): CourseView<T, V> {
  return function _composedView(obj: V): Nullable<T> {
    const v: Nullable<U> = view1(obj);

    if (R.isNil(v)) {
      return null;
    }

    return view2(v);
  };
}

export const gridColumnView = (part: IGridPart, columnIndex: number): NullablePartPosition =>
  R.pathOr(null, ["content", "columns", "content", columnIndex], part);
